import React, { useState } from "react";
import { Link } from "react-router-dom";

/**
 * PublicLinkCard — ME-tab tile showing the operator's shareable
 * `leaguehub.co/u/{username}` URL with COPY + native-share buttons.
 *
 * Mirrors the visual treatment of `ReferralCard` so the two share
 * surfaces sit side-by-side without one feeling like an afterthought.
 *
 * If the user record is missing `username` (very old account, or the
 * lazy backfill hasn't run yet) we hide the card silently — better
 * than rendering an empty URL.
 */
export function PublicLinkCard({ user, onToast }) {
  const [copied, setCopied] = useState(false);
  const username = user?.username;
  if (!username) return null;

  // Always render the canonical leaguehub.co host in the *displayed*
  // URL — that's what users actually paste in their Discord bios. The
  // share/copy still uses the current origin so it works on preview.
  const displayUrl = `leaguehub.co/u/${username}`;
  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : "https://leaguehub.co"}/u/${username}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      onToast?.("🔗 Public link copied!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      onToast?.(shareUrl);
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${user.name} on LEAGUE HUB`,
          text: `Check out my operator profile on LEAGUE HUB`,
          url: shareUrl,
        });
        return;
      } catch {/* user cancelled — fall through to copy */}
    }
    copy();
  };

  return (
    <div
      className="glass rounded-3xl p-5 border border-[#14b8a6]/20"
      data-testid="dash-public-link-card"
    >
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
        <i className="fas fa-link mr-1" /> YOUR PUBLIC LINK
      </p>
      <p className="text-[11px] text-white/55 mt-2 leading-relaxed">
        Drop this in your Discord/X bio. Viewers see your MMR, primary game, teams, and platforms.
      </p>
      <div className="mt-3 flex items-stretch gap-2">
        <input
          readOnly
          value={displayUrl}
          data-testid="dash-public-link-input"
          onClick={(e) => e.target.select()}
          className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-[12px] font-mono text-white truncate focus:outline-none focus:border-[#14b8a6]/60"
        />
        <button
          type="button"
          onClick={copy}
          data-testid="dash-public-link-copy"
          className={`text-[10px] tracking-widest px-3 rounded-xl border whitespace-nowrap ${
            copied
              ? "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/40"
              : "bg-white/5 text-white border-white/10 hover:bg-white/10"
          }`}
        >
          {copied ? "COPIED" : "COPY"}
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={share}
          data-testid="dash-public-link-share"
          className="flex-1 text-[10px] tracking-widest px-3 py-2 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
        >
          <i className="fas fa-share-nodes mr-1" />SHARE
        </button>
        <Link
          to={`/u/${username}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="dash-public-link-preview"
          className="flex-1 text-[10px] tracking-widest px-3 py-2 rounded-xl bg-white/5 text-white/80 border border-white/10 hover:bg-white/10 text-center"
        >
          <i className="fas fa-external-link-alt mr-1" />PREVIEW
        </Link>
      </div>
    </div>
  );
}

export default PublicLinkCard;
